import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Sparkles } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/sports-betting/nba`;
const pct = (x) => (x == null ? '—' : `${Math.round(x * 100)}%`);
const when = (iso) => (iso ? new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : 'never');

const Players = ({ players }) => {
  const list = Object.values(players || {}).sort((a, b) => b.shift - a.shift);
  if (!list.length) return null;
  return (
    <div data-testid="nba-learn-players">
      <p className="text-xs font-bold text-zinc-300 mt-2">Players</p>
      <ul className="text-xs space-y-0.5">{list.map((p) => (
        <li key={p.name} className="flex justify-between gap-2">
          <span>{p.name} <span className="text-zinc-500">· hit {p.hits}/{p.n}</span></span>
          <span className={p.shift > 0 ? 'text-emerald-300' : 'text-red-400'}>{p.shift > 0 ? 'boosted' : 'trimmed'} {Math.abs(Math.round(p.shift * 100))}%</span>
        </li>))}</ul>
    </div>
  );
};

const Buckets = ({ buckets }) => {
  const rows = Object.entries(buckets || {});
  if (!rows.length) return null;
  return (
    <div data-testid="nba-learn-buckets">
      <p className="text-xs font-bold text-zinc-300 mt-2">Line vs the player's average</p>
      {rows.map(([st, bs]) => (
        <p key={st} className="text-xs text-zinc-300"><span className="capitalize">{st}</span>: {bs.map((b) => `${b.range} hit ${pct(b.hit_rate)} (${b.n})`).join(' · ')}</p>
      ))}
    </div>
  );
};

// NBA: feed the graded legs on your tickets into tonight's picks (also runs on its own every night).
export const NbaTicketLearning = () => {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { axios.get(`${API}/ticket-learning`).then((r) => setD(r.data)).catch(() => {}); }, []);
  const run = async () => {
    setBusy(true);
    try {
      const r = (await axios.post(`${API}/learn-from-tickets`)).data;
      setD(r);
      toast.success(`Learned from ${r.recent?.tickets || 0} NBA tickets (${r.recent?.won || 0} won, ${r.recent?.lost || 0} lost) — rebuilding tonight's picks`);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setBusy(false);
  };
  const r = d?.recent || {};
  return (
    <section className="rounded-xl border border-orange-500/40 bg-orange-950/20 p-3" data-testid="nba-learn">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-bold text-orange-300">NBA — learn from tonight's tickets</p>
          <p className="text-[11px] text-zinc-400">Uses every finished NBA ticket (newest counted most) to tune the picks by stat type, by player, and by how far under the player's average the line was. Runs on its own every night after the games. Last run: {when(d?.at)}</p>
        </div>
        <button type="button" onClick={run} disabled={busy} data-testid="nba-learn-run"
          className="shrink-0 inline-flex items-center gap-1.5 rounded-lg bg-orange-500 px-3 py-2 text-xs font-bold text-black hover:bg-orange-400 disabled:opacity-50">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}Learn from tonight
        </button>
      </div>
      {d?.at && (
        <div className="mt-2">
          <p className="text-xs text-zinc-200" data-testid="nba-learn-recent">Last 36 hours: <b>{r.tickets || 0}</b> NBA tickets · <b className="text-emerald-300">{r.won || 0} won</b> · <b className="text-red-400">{r.lost || 0} lost</b> · legs hit {r.legs_hit || 0}/{r.legs || 0}</p>
          {(d.notes || []).length > 0 && <ul className="mt-1 list-disc pl-5 text-xs text-zinc-200 space-y-0.5" data-testid="nba-learn-notes">{d.notes.map((n) => <li key={n}>{n}</li>)}</ul>}
          <Players players={d.players} />
          <Buckets buckets={d.buckets} />
        </div>
      )}
    </section>
  );
};
